import { motion, AnimatePresence } from "framer-motion";
import { X, Lock, Play } from "lucide-react";

export function VideoModal({ video, onClose, onSubscribe, onPPV }) {
  if (!video) return null;

  const embedUrl = video.source_url;
  const isNative =
    ["file", "url"].includes(video.source_type) ||
    /\.(mp4|webm|mov|m3u8)(\?.*)?$/i.test(embedUrl || "");
  const ppvLabel =
    video.ppv_type === "event"
      ? `Buy Ticket · $${Number(video.ppv_price).toFixed(2)}`
      : `Rent · $${Number(video.ppv_price).toFixed(2)}`;

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/90 backdrop-blur-lg px-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        data-testid="video-modal"
      >
        <motion.div
          className="relative w-full max-w-4xl"
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.97, opacity: 0 }}
          transition={{ type: "spring", stiffness: 220, damping: 24 }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={onClose}
            className="absolute -top-10 right-0 text-white/60 hover:text-white transition-colors"
            data-testid="video-close-btn"
          >
            <X size={24} />
          </button>

          {video.locked ? (
            <div className="border border-white/10 bg-[#0A0A0A] aspect-video flex flex-col items-center justify-center text-center px-6">
              <Lock size={40} className="text-gold mb-4" />
              <p className="font-mono-imn text-xs tracking-[0.3em] text-gold uppercase mb-3">
                {video.ppv ? (video.ppv_type === "event" ? "Live Event · Pay-Per-View" : "Pay-Per-View Rental") : "Premium Content"}
              </p>
              <h3 className="font-display text-3xl md:text-4xl font-bold uppercase mb-4">
                {video.title}
              </h3>
              <p className="text-white/60 max-w-md mb-2 text-sm">
                {video.description}
              </p>
              {video.ppv && video.ppv_type === "rental" && (
                <p className="font-mono-imn text-[11px] tracking-widest text-white/40 uppercase mb-6">
                  {video.rental_hours}-hour access after purchase
                </p>
              )}
              {video.ppv ? (
                <button
                  onClick={() => onPPV?.(video)}
                  data-testid="modal-ppv-btn"
                  className="bg-gold text-black font-semibold px-8 py-3 text-sm uppercase tracking-wider hover:opacity-90 active:scale-95 transition-all mt-4"
                >
                  {ppvLabel}
                </button>
              ) : (
                <button
                  onClick={onSubscribe}
                  data-testid="modal-subscribe-btn"
                  className="bg-white text-black font-semibold px-8 py-3 text-sm uppercase tracking-wider hover:bg-white/90 active:scale-95 transition-all mt-4"
                >
                  Subscribe to Watch
                </button>
              )}
            </div>
          ) : embedUrl ? (
            isNative ? (
              <div className="aspect-video bg-black border border-white/10">
                <video
                  className="w-full h-full"
                  src={embedUrl}
                  poster={video.thumbnail}
                  controls
                  autoPlay
                  playsInline
                  data-testid="video-native"
                />
              </div>
            ) : (
              <div className="aspect-video bg-black border border-white/10">
                <iframe
                  className="w-full h-full"
                  src={embedUrl}
                  title={video.title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  data-testid="video-iframe"
                />
              </div>
            )
          ) : (
            <div className="border border-white/10 bg-[#0A0A0A] aspect-video flex items-center justify-center">
              <Play size={40} className="text-white/40" />
            </div>
          )}

          {!video.locked && (
            <div className="mt-5">
              <h3 className="font-display text-2xl font-bold uppercase">
                {video.title}
              </h3>
              <p className="text-white/50 text-sm mt-1">{video.description}</p>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
